import { z } from "zod";
import { referralCodeSchema } from "./community-v1";
import {
  communityAuraSnapshotSchema,
  communityAuraStandingSchema,
  communityBadgeRaritySchema,
  communityPublicIdentitySchema,
  genesisMemberInviteStatusSchema,
  MEMBER_COLOR_COUNT,
} from "./community-genesis-v1";

/** Most invitee colours a profile returns: the invite dot field's largest size. */
export const PROFILE_INVITEE_COLOR_LIMIT = 200;

/**
 * Member progress for the Community identity service: referral facts and the achievement evaluation
 * beside `/me`. Kept out of `community-v1`'s profile so existing strict profile parsers keep working.
 *
 * Completing an invitation means a referred person proved a wallet and linked an X account. It is
 * deliberately separate from the leaderboard's ranking rule (which may require an older X account):
 * every attributed person appears in `completed` or `pending`, so no signup disappears from progress.
 */

const countSchema = z.number().refine(Number.isInteger).meta({ type: "integer" }).nonnegative();
const achievementIdSchema = z.string().regex(/^[a-z][a-z0-9]*(\.[a-z0-9_]+)+$/);
const campaignIdSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/);
const sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);

export const communityAchievementStatusSchema = z.enum(["locked", "in_progress", "earned", "revoked"]);

/** What the member can do next; the evaluator chooses it, the UI only renders it. */
export const communityAchievementCtaSchema = z.enum([
  "copy_invite_link", "complete_signup", "link_discord", "submit_project", "none",
]);

export const communityAchievementPrerequisiteSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9]*(\.[a-z0-9_]+)+$/),
  label: z.string().min(1).max(96),
  met: z.boolean(),
}).strict();

export const communityAchievementSchema = z.object({
  id: achievementIdSchema,
  version: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive(),
  kind: z.literal("milestone"),
  title: z.string().min(1).max(64),
  /** One line: what earns it. */
  instruction: z.string().min(1).max(160),
  status: communityAchievementStatusSchema,
  /** The metric's current value and the target; `current` is never clamped to `target`. */
  progress: z.object({ current: countSchema, target: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive() }).strict(),
  prerequisites: z.array(communityAchievementPrerequisiteSchema).max(8),
  /** Criteria that still block the award, in display order; empty when earned. */
  unmet: z.array(z.string().min(1).max(96)).max(8),
  cta: communityAchievementCtaSchema,
  /** Badges carry no item or allocation reward; they count toward Aura while earned. */
  reward: z.object({ kind: z.literal("none") }).strict(),
  /** Live rarity among eligible members; `null` until a snapshot exists. */
  rarity: communityBadgeRaritySchema.nullable(),
  /**
   * Earned holder badges only: whether a snapshot member has an earned action badge, independent
   * of scoring or collection influence; `null` for other or unearned badges.
   */
  active: z.boolean().nullable(),
}).strict();

export const communityAchievementsSchema = z.object({
  campaignId: campaignIdSchema,
  /** SHA-256 of the evaluated definition set, so a projection can be replayed against it. */
  definitionsHash: sha256HexSchema,
  items: z.array(communityAchievementSchema),
}).strict();

/**
 * A mini social profile under `/genesis/<code>`. Public by design: it never carries a person id,
 * wallet, referee identity, Discord account or moderation state. `identity` is the member's X
 * identity or their alias (see `communityPublicIdentitySchema`); it is `null` only for an unknown code.
 */
export const communityPublicProfileResponseSchema = z.object({
  code: referralCodeSchema,
  valid: z.boolean(),
  identity: communityPublicIdentitySchema.nullable(),
  standing: communityAuraStandingSchema,
  completedReferrals: countSchema,
  /**
   * The colour (`memberColorIndex`) of each completed invitee, earliest first, at most
   * `PROFILE_INVITEE_COLOR_LIMIT`: lights the invite dots without naming who joined.
   */
  inviteeColors: z.array(z.number().int().min(0).max(MEMBER_COLOR_COUNT - 1)).max(PROFILE_INVITEE_COLOR_LIMIT),
  /** Earned badges in catalogue order, each with its live rarity. */
  achievements: z.array(z.object({
    id: achievementIdSchema,
    title: z.string().min(1).max(64),
    rarity: communityBadgeRaritySchema.nullable(),
    /** Holder badges only: earned-action activity, not scoring or collection influence; `null` otherwise. */
    active: z.boolean().nullable(),
  }).strict()),
  snapshot: communityAuraSnapshotSchema.nullable(),
}).strict();

export const communityReferralProgressSchema = z.object({
  code: referralCodeSchema,
  /** Invited people who completed wallet + X signup and are not disqualified. */
  completed: countSchema,
  /** Invited people who signed up with a wallet but have not linked X yet. */
  pending: countSchema,
  /** Completed signups that also meet the X-account-age rule the higher invitation badges count. */
  qualified: countSchema,
  /** The effective age rule, disclosed so a young linked X is not read as a broken signup. */
  minXAccountAgeDays: countSchema,
  /** Whether this member's invitation link records new members as their invitees; never why not. */
  inviteStatus: genesisMemberInviteStatusSchema,
}).strict();

export const communityProgressResponseSchema = z.object({
  standing: communityAuraStandingSchema,
  identity: communityPublicIdentitySchema,
  referral: communityReferralProgressSchema,
  achievements: communityAchievementsSchema,
  /** Owner only: Aura from collection activation in the latest snapshot, already capped. Never public. */
  communityAura: countSchema,
  snapshot: communityAuraSnapshotSchema.nullable(),
}).strict();

export type CommunityAchievementStatus = z.infer<typeof communityAchievementStatusSchema>;
export type CommunityAchievementCta = z.infer<typeof communityAchievementCtaSchema>;
export type CommunityAchievement = z.infer<typeof communityAchievementSchema>;
export type CommunityAchievements = z.infer<typeof communityAchievementsSchema>;
export type CommunityPublicProfileResponse = z.infer<typeof communityPublicProfileResponseSchema>;
export type CommunityReferralProgress = z.infer<typeof communityReferralProgressSchema>;
export type CommunityProgressResponse = z.infer<typeof communityProgressResponseSchema>;
